import { capitalize, escape, map } from 'lodash-es';
import type { Reference, ReferenceClean } from '@platform/entities/entity.class';
import type { ColumnParams } from '@platform/shared/rpc/types';
import { DataType } from '@platform/shared/rpc/types';
import { formatTStoDATE, formatTStoDATETIME } from '@platform/shared/lib/datetime';

export function formatShortFio(
    surname: string | undefined,
    firstname: string | undefined,
    secname: string | undefined
): string {
    const sur = (surname || '').trim();
    if (!sur) return '';
    const formatted = [capitalize(surname?.trim())];
    if (firstname?.trim()) {
        formatted.push(firstname.trim()[0].toLocaleUpperCase() + '.');
        if (secname?.trim()) {
            formatted.push(secname.trim()[0].toLocaleUpperCase() + '.');
        }
    }
    return formatted.join(' ');
}

export function formatFullFio(
    surname: string | undefined,
    firstname: string | undefined,
    secname: string | undefined
): string {
    return [surname, firstname, secname]
        .filter((str) => !!str)
        .map((str) => capitalize(str?.trim()))
        .join(' ');
}
type UnknownReference<T = string> = { value?: T } | undefined | null | T;

type TValNormalize<T> = T extends Reference ? Reference | null : T;
type TReturnVal<P> = P extends Reference ? string : P extends undefined ? null : P | null;
export function normalize<P>(val: TValNormalize<P>): TReturnVal<P> {
    if (typeof val === 'undefined') {
        return null as TReturnVal<P>;
    } else if (val === null) {
        return null as TReturnVal<P>;
    } else if (typeof val === 'object' && 'value' in val) {
        return ((val as { value?: string })?.value ?? null) as TReturnVal<P>;
    } else if (typeof val === 'string') {
        return val as TReturnVal<P>;
    } else if (typeof val === 'number') {
        return val as TReturnVal<P>;
    } else if (typeof val === 'boolean') {
        return val as TReturnVal<P>;
    } else {
        return null as TReturnVal<P>;
    }
}
normalize.equals = function <P>(val: TValNormalize<P>, val2: TValNormalize<P>) {
    return normalize(val) === normalize(val2);
};
export function getReferenceOf<T>(
    value: T,
    name?: string,
    label?: string
): { value: T; name?: string; label?: string } {
    return { value, name, label: label || name };
}
export function getReferenceDestruct({ value, name, label }: ReferenceClean) {
    return {
        value,
        name,
        label: label ?? name,
    };
}

export function getLabel(val: Reference | null | undefined): string | null {
    return val ? (typeof val === 'object' ? val.label : null) : null;
}
export function getName(val: Reference | string | undefined): string | null {
    return val ? (typeof val === 'object' ? val.name : val) : null;
}

export function columnFieldHumanRepr(
    value: unknown,
    { dataType, enumType }: { dataType: DataType | [DataType] } & Pick<ColumnParams, 'enumType'>,
    nullReplacement = 'не указан',
    referenceNameKey: 'name' | 'label' = 'label'
): string | undefined {
    if (value === null || value === undefined) return nullReplacement;
    switch (dataType) {
        case DataType.UUID:
            return value && typeof value === 'object' && referenceNameKey in value
                ? (value as { label?: string; name?: string })?.[referenceNameKey] || nullReplacement
                : nullReplacement;
        case DataType.UUIDLIST:
            return map(value, (item: any) => item?.label ?? item?.name ?? item).join(', ');
        case DataType.NUMERIC:
        case DataType.BIGINT:
        case DataType.VARCHAR: {
            if (enumType) {
                const enumValue = normalize(value as UnknownReference) as string | null;
                return enumValue ? enumType[enumValue].name || enumValue : nullReplacement;
            }
            return typeof value === 'string'
                ? value?.toString() || nullReplacement
                : typeof value === 'object' && 'name' in value
                  ? (value as { name?: string })?.name
                  : nullReplacement;
        }
        case DataType.BOOLEAN:
            return value ? 'да' : 'нет';
        case DataType.DATE:
            if (typeof value === 'number' || typeof value === 'string' || value instanceof Date)
                return formatTStoDATE(value);
            return value?.toString() || nullReplacement;
        case DataType.TIMESTAMP:
            if (typeof value === 'number' || typeof value === 'string' || value instanceof Date)
                return formatTStoDATETIME(value) ?? undefined;
            if (Array.isArray(value) && value.length === 2) {
                return value
                    .map((v) => columnFieldHumanRepr(v, { dataType, enumType }, nullReplacement, referenceNameKey))
                    .join(' - ');
            }
            return value?.toString() || nullReplacement;
        case DataType.JSON:
            return typeof value === 'string' ? value : JSON.stringify(value);
        default:
            return value?.toString() ?? nullReplacement;
    }
}

export function formatNounMasculine(noun: string, count: number) {
    const c = count % 100;
    if (count % 10 === 0 || (c > 4 && c < 20)) return noun + 'ов';
    if (count % 10 === 1) return noun;
    if (count % 10 > 1 && count % 10 < 5) return noun + 'а';
    return noun + 'ов';
}

export function formatNoun(noun: [string, string, string], count: number) {
    const c = count % 100;
    if (count % 10 === 0 || (c > 4 && c < 20)) return noun[2];
    if (count % 10 === 1) return noun[0];
    if (count % 10 > 1 && count % 10 < 5) return noun[1];
    return noun[2];
}
export function formatNounWithCount(noun: [string, string, string], count: number) {
    return count + ' ' + formatNoun(noun, count);
}

export function renderWithIcon(icon: string | null, text: string, variant: null | 'outlined' = 'outlined') {
    const escaped = escape(text ?? '');
    if (!icon) return `<span>${escaped}</span>`;
    const cssClassSuffix = variant ? '-' + variant : '';
    return `<span><i class="material-icons${cssClassSuffix}">${icon}</i>&nbsp;${escaped}</span>`;
}

export function getDynamicProps<T extends object>(getObj: () => T) {
    const obj = getObj();
    const result = {} as any | T;
    for (const [key, value] of Object.entries(obj)) {
        result[key] = value();
    }

    return result;
}
// Если значение это функция, то вызываем её и возвращаем результат. Иначе просто возвращаем само значение
export type prepare<T, G = any> = T | ((arg: G) => T);
export function prepareValue<T>(value: prepare<T>): T {
    if (typeof value === 'function') {
        return (value as () => T)();
    }
    return value;
}

export function formatNumber2Digits(value: number, digits: number = 2): string {
    return (
        Intl.NumberFormat('ru-RU', {
            minimumFractionDigits: 0,
            maximumFractionDigits: digits,
        }).format(value ?? 0) ?? 0
    );
}
